import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../stores/app-store';
import { Upload, Image as ImageIcon, Trash2, Sparkles, RefreshCw } from 'lucide-react';
import type { Project, BookMetadata } from '../types';
import { notify } from '../lib/toast';
import { api } from '../lib/api';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { StylePicker } from './style-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

interface BookMetadataModalProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Aba em que o modal abre (ex.: 'cover' quando vem do botão "Trocar Capa"). */
  initialTab?: 'basic' | 'cover' | 'publishing';
}

export function BookMetadataModal({ project, open, onOpenChange, initialTab = 'basic' }: BookMetadataModalProps) {
  const { t } = useTranslation();
  const updateProject = useAppStore((s) => s.updateProject);
  const [metadata, setMetadata] = useState<BookMetadata>(project.metadata);
  const [activeTab, setActiveTab] = useState<'basic' | 'cover' | 'publishing'>(initialTab);
  // ao (re)abrir, começa na aba pedida (cover quando vem de "Trocar Capa")
  useEffect(() => { if (open) setActiveTab(initialTab); }, [open, initialTab]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AI cover generation state
  const [coverPrompt, setCoverPrompt] = useState('');
  const [generatingCovers, setGeneratingCovers] = useState(false);
  const [generatedCovers, setGeneratedCovers] = useState<string[]>([]);
  const [selectedCoverIndex, setSelectedCoverIndex] = useState<number | null>(null);

  // Project-wide art style (projects.mangaStyle) — the default that permeates
  // every AI image in this book/manga. Lives outside `metadata`, so it's loaded
  // and saved directly via the API.
  const [artStyle, setArtStyle] = useState('');
  const [savedArtStyle, setSavedArtStyle] = useState('');
  useEffect(() => {
    if (!open) return;
    api.getProject(project.id)
      .then((r) => { const s = r.project.mangaStyle ?? ''; setArtStyle(s); setSavedArtStyle(s); })
      .catch(() => {});
  }, [open, project.id]);

  const handleSave = async () => {
    updateProject(project.id, { metadata });
    if (artStyle !== savedArtStyle) {
      try { await api.updateProject(project.id, { mangaStyle: artStyle }); } catch { /* non-fatal */ }
    }
    onOpenChange(false);
  };

  // Fechar sem querer (clique fora / Esc) não pode jogar fora edições nem
  // capas recém-geradas — pede confirmação antes de descartar.
  const isDirty =
    JSON.stringify(metadata) !== JSON.stringify(project.metadata) ||
    generatedCovers.length > 0 ||
    artStyle !== savedArtStyle;
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && (isDirty || generatingCovers)) {
      const ok = confirm(
        generatingCovers
          ? t('meta.closeWhileGenerating', 'As capas ainda estão sendo geradas. Fechar mesmo assim?')
          : t('meta.discardChanges', 'Você tem alterações não salvas. Descartar?')
      );
      if (!ok) return;
    }
    onOpenChange(nextOpen);
  };

  const handleChange = (field: keyof BookMetadata, value: string | string[]) => {
    setMetadata((prev) => ({ ...prev, [field]: value }));
  };

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify.error(t('meta.coverTooLarge', 'Cover image must be less than 5MB'));
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      notify.error(t('meta.invalidCoverType', 'Please upload an image file'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setMetadata((prev) => ({ ...prev, coverImage: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveCover = () => {
    setMetadata((prev) => ({ ...prev, coverImage: undefined }));
  };

  const handleGenerateCovers = async () => {
    setGeneratingCovers(true);
    setGeneratedCovers([]);
    setSelectedCoverIndex(null);
    try {
      const covers = await api.generateCover({
        prompt: coverPrompt,
        title: metadata.title,
        author: metadata.author,
        genre: metadata.genre,
        projectId: project.id,
      });
      setGeneratedCovers(covers);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('bookMetadata.generateCoversError', 'Falha ao gerar capas');
      notify.error(message);
    } finally {
      setGeneratingCovers(false);
    }
  };

  const handleSelectGeneratedCover = (dataUrl: string, index: number) => {
    setSelectedCoverIndex(index);
    setMetadata((prev) => ({ ...prev, coverImage: dataUrl }));
  };

  const handleKeywordsChange = (value: string) => {
    const keywords = value.split(',').map((k) => k.trim()).filter(Boolean);
    setMetadata((prev) => ({ ...prev, keywords }));
  };

  return (
    <ResponsiveModal open={open} onOpenChange={handleOpenChange}>
      <ResponsiveModalContent className="sm:max-w-2xl">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>{t('meta.bookDetails', 'Book Details')}</ResponsiveModalTitle>
        </ResponsiveModalHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="w-full">
            <TabsTrigger value="basic" className="flex-1">{t('meta.basicInfo', 'Basic Info')}</TabsTrigger>
            <TabsTrigger value="cover" className="flex-1">{t('meta.coverImage', 'Cover Image')}</TabsTrigger>
            <TabsTrigger value="publishing" className="flex-1">{t('meta.publishing', 'Publishing')}</TabsTrigger>
          </TabsList>

          <div className="mt-4 space-y-4 overflow-y-auto md:max-h-[55vh] md:pr-1">
            {/* Basic */}
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="meta-title">{t('meta.title')}</Label>
                <Input
                  id="meta-title"
                  value={metadata.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder={t('projects.untitled')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-subtitle">{t('meta.subtitle', 'Subtitle')}</Label>
                <Input
                  id="meta-subtitle"
                  value={metadata.subtitle || ''}
                  onChange={(e) => handleChange('subtitle', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-author">{t('meta.author')}</Label>
                <Input
                  id="meta-author"
                  value={metadata.author}
                  onChange={(e) => handleChange('author', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-description">{t('meta.description')}</Label>
                <Textarea
                  id="meta-description"
                  value={metadata.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={4}
                  placeholder={t('meta.descriptionPlaceholder', 'A brief description of your book...')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-genre">{t('meta.genre')}</Label>
                <Input
                  id="meta-genre"
                  value={metadata.genre}
                  onChange={(e) => handleChange('genre', e.target.value)}
                  placeholder={t('meta.genrePlaceholder', 'Fiction, Mystery, Romance...')}
                />
              </div>
              {/* Project-wide art style — the default that every AI image follows. */}
              <div className="space-y-1.5 rounded-lg border border-border bg-muted/30 p-3">
                <Label>{t('meta.artStyle', 'Estilo de arte (padrão do projeto)')}</Label>
                <StylePicker value={artStyle} onChange={setArtStyle} />
                <p className="text-xs text-muted-foreground">
                  {t('meta.artStyleHint', 'Aplicado a todas as ilustrações, personagens e capas gerados por IA neste projeto. É salvo ao clicar em Salvar.')}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>{t('meta.language')}</Label>
                <Select value={metadata.language} onValueChange={(v) => handleChange('language', v as string)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="pt-BR">Português (Brasil)</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="it">Italiano</SelectItem>
                    <SelectItem value="ja">日本語</SelectItem>
                    <SelectItem value="zh">中文</SelectItem>
                    <SelectItem value="ko">한국어</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* Cover */}
            <TabsContent value="cover" className="space-y-4">
              <div className="flex flex-col items-center gap-4">
                <div className="flex aspect-[2/3] w-48 items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-border bg-muted">
                  {metadata.coverImage ? (
                    <img src={metadata.coverImage} alt="Book cover" className="h-full w-full object-cover" />
                  ) : (
                    <div className="p-4 text-center">
                      <ImageIcon size={48} className="mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm text-muted-foreground">{t('meta.noCover', 'No cover image')}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => fileInputRef.current?.click()}>
                    <Upload size={18} />
                    {metadata.coverImage ? t('meta.changeCover', 'Change Cover') : t('meta.uploadCover', 'Upload Cover')}
                  </Button>
                  {metadata.coverImage && (
                    <Button variant="outline" onClick={handleRemoveCover} className="text-destructive hover:text-destructive">
                      <Trash2 size={18} />
                      {t('common.remove', 'Remove')}
                    </Button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
              </div>

              {/* AI Cover Generation */}
              <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-primary" />
                  <h4 className="font-medium text-sm text-foreground">{t('bookMetadata.generateWithAi', 'Gerar com IA')}</h4>
                </div>
                <Textarea
                  value={coverPrompt}
                  onChange={(e) => setCoverPrompt(e.target.value)}
                  placeholder={t('bookMetadata.coverPromptPlaceholder', 'Descreva a capa... ou deixe a IA criar a partir do título e gênero. Ex: céu estrelado, floresta mística, paleta fria em tons de azul e prata.')}
                  rows={3}
                  className="resize-none text-sm"
                  disabled={generatingCovers}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleGenerateCovers}
                    disabled={generatingCovers}
                    className="gap-2"
                    size="sm"
                  >
                    {generatingCovers ? (
                      <>
                        <RefreshCw size={14} className="animate-spin" />
                        {t('bookMetadata.generatingCovers', 'Gerando...')}
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} />
                        {t('bookMetadata.generate4Covers', 'Gerar 4 capas')}
                      </>
                    )}
                  </Button>
                  {generatedCovers.length > 0 && !generatingCovers && (
                    <Button
                      variant="outline"
                      onClick={handleGenerateCovers}
                      size="sm"
                      className="gap-2"
                    >
                      <RefreshCw size={14} />
                      {t('bookMetadata.regenerate', 'Regenerar')}
                    </Button>
                  )}
                </div>

                {generatingCovers && (
                  <p className="text-xs text-muted-foreground">
                    {t('bookMetadata.generationTimeHint', 'A geração pode levar entre 10 e 30 segundos...')}
                  </p>
                )}

                {generatedCovers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">{t('bookMetadata.clickToSelectCover', 'Clique em uma capa para selecioná-la:')}</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {generatedCovers.map((cover, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleSelectGeneratedCover(cover, i)}
                          className={[
                            'relative overflow-hidden rounded-md border-2 aspect-[2/3] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                            selectedCoverIndex === i
                              ? 'border-primary ring-2 ring-primary'
                              : 'border-border hover:border-primary/60',
                          ].join(' ')}
                        >
                          <img
                            src={cover}
                            alt={t('bookMetadata.generatedCoverAlt', 'Capa gerada {{index}}', { index: i + 1 })}
                            className="h-full w-full object-cover"
                          />
                          {selectedCoverIndex === i && (
                            <div className="absolute inset-0 flex items-center justify-center bg-primary/20">
                              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                                {t('bookMetadata.coverSelected', 'Selecionada')}
                              </span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm">
                <h4 className="mb-2 font-medium text-foreground">{t('meta.coverGuidelines', 'Cover Guidelines')}</h4>
                <ul className="list-inside list-disc space-y-1 text-muted-foreground">
                  <li>{t('meta.coverSize', 'Recommended size: 1600 x 2400 pixels (2:3 ratio)')}</li>
                  <li>{t('meta.coverFormat', 'Supported formats: JPG, PNG, WebP')}</li>
                  <li>{t('meta.coverMaxSize', 'Maximum file size: 5MB')}</li>
                  <li>{t('meta.coverKdp', 'KDP requires minimum 300 DPI for print')}</li>
                </ul>
              </div>
            </TabsContent>

            {/* Publishing */}
            <TabsContent value="publishing" className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="meta-isbn">{t('meta.isbn', 'ISBN')}</Label>
                <Input
                  id="meta-isbn"
                  value={metadata.isbn || ''}
                  onChange={(e) => handleChange('isbn', e.target.value)}
                  placeholder="978-0-000-00000-0"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-publisher">{t('meta.publisher', 'Publisher')}</Label>
                <Input
                  id="meta-publisher"
                  value={metadata.publisher || ''}
                  onChange={(e) => handleChange('publisher', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-publishDate">{t('meta.publishDate', 'Publication Date')}</Label>
                <Input
                  id="meta-publishDate"
                  type="date"
                  value={metadata.publishDate || ''}
                  onChange={(e) => handleChange('publishDate', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-copyright">{t('meta.copyright', 'Copyright Notice')}</Label>
                <Textarea
                  id="meta-copyright"
                  value={metadata.copyright || ''}
                  onChange={(e) => handleChange('copyright', e.target.value)}
                  rows={3}
                  placeholder={`© ${new Date().getFullYear()} ${metadata.author || 'Author Name'}. All rights reserved.`}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="meta-keywords">
                  {t('meta.keywords', 'Keywords')}
                  <span className="ml-1 font-normal text-muted-foreground">
                    ({t('meta.separateCommas', 'separate with commas')})
                  </span>
                </Label>
                <Input
                  id="meta-keywords"
                  value={metadata.keywords?.join(', ') || ''}
                  onChange={(e) => handleKeywordsChange(e.target.value)}
                  placeholder="thriller, mystery, detective, crime fiction"
                />
                <p className="text-xs text-muted-foreground">
                  {t('meta.keywordsHelp', 'Keywords help readers discover your book on Amazon and other platforms')}
                </p>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave}>{t('common.save')}</Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
